/* @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { Dialog, DialogContent, DialogTitle } from "@mui/material";
import { ReactComponent as CloseIcon } from "features/wc/assets/close.svg";
import { ReactComponent as Circle } from "features/wc/assets/circle.svg";
import { ReactComponent as Cross } from "features/wc/assets/cross.svg";
import AppIconButton from "components/AppIconButton";
import UsageLabel from "features/wc/components/UsageLabel";
import React from "react";
import { useTranslation } from "react-i18next";
import { AdjustmentWcInfoList } from "features/wc/types";
import Colors from "util/colors";

const colors = Colors.get();

const styles = {
  // タイトル
  title: css({
    position: "relative",
    height: "57px",
    textAlign: "center",
    borderBottom: `1px solid ${colors.separator}`,
    fontWeight: 600,
    fontSize: "16px",
    lineHeight: "24px",
  }),

  // 閉じるボタン
  close: css({
    position: "absolute",
    right: "11px",
    top: "50%",
    transform: "translateY(-50%)",
  }),

  // コンテンツ
  content: css({
    padding: "0px 20px 20px 20px",
  }),
};

const Massage = styled("div")({
  marginTop: "20px",
  marginBottom: "30px",
  fontSize: "14px",
  fontWeight: 300,
});
const StatePanel = styled("div")({
  marginBottom: "30px",
});
const StateTitle = styled("div")({
  fontSize: "18px",
  fontWeight: 600,
  lineHeight: "18px",
  marginBottom: "12px",
});
const StateMassage = styled("div")({
  fontSize: "14px",
  fontWeight: 300,
  lineHeight: "14px",
  marginBottom: "12px",
});
const StateContent = styled("div")({
  backgroundColor: colors.background,
  minHeight: "94px",
  padding: "14px 18px 8px 18px"
});
const StateContentPanel = styled("div")({
  backgroundColor: colors.white,
  minHeight: "30px",
  padding: "0px 10px 0px 10px",
  marginBottom: "6px",
  borderRadius: "3px",
  display: "flex",
  alignItems: "center",
});
const StateContentInfo = styled("div")({
  fontSize: "12px",
  fontWeight: 300,
});
const OpenLabel = styled("div")({
  minWidth: "48px",
  fontWeight: 600,
  fontSize: "12px",
  textAlign: "center",
  marginRight:"10px",
  color: colors.open,
});
const CloseLabel = styled("div")({
  minWidth: "48px",
  fontWeight: 600,
  fontSize: "12px",
  textAlign: "center",
  marginRight:"10px",
  color: colors.close,
});
const DescriptionPanel = styled("div")({
});
const DescriptionTitle = styled("div")({
  fontSize: "18px",
  fontWeight: 600,
  lineHeight: "18px",
  marginBottom: "10px",
});
const DescriptionContent = styled("div")({
  backgroundColor: colors.background,
  padding: "9px 18px 9px 18px"
});

/**
 * プロパティ定義
 */
type Props = {
  /** ダイアログの表示|非表示 */
  isOpen: boolean;
  /** 使い方表示用のリスト */
  adjustmentWcInfoList: AdjustmentWcInfoList | null;
  /** ダイアログを閉じた時のイベントハンドラ */
  onClose: (result: boolean) => void;
};

/**
 * 使い方ポップアップ
 * @param param コンポーネントのProps
 */
export default React.memo(({ isOpen, adjustmentWcInfoList, onClose }: Props) => {
  const { t } = useTranslation("トイレ可視化");

  /** 閉じるボタンの押下時 */
  const handleClose = () => {
    onClose(false);
  };

  return (
    <Dialog
      open={isOpen}
      PaperProps={{ style: { borderRadius: "10px", maxWidth: "349px" } }}
      fullWidth
    >
      {/* タイトル */}
      <DialogTitle css={styles.title}>
        {/* テキスト */}
        <span>{t("使い方.タイトル")}</span>
        {/* 閉じるボタン */}
        <AppIconButton css={styles.close} color="primary" onClick={handleClose} id="ODBCOUNT-CLOSE">
          <CloseIcon />
        </AppIconButton>
      </DialogTitle>

      {/* コンテンツ */}
      <DialogContent css={styles.content}>
        <Massage>{t("使い方.メッセージ")}</Massage>
        <StatePanel>
          <StateTitle>{t("使い方.利用状況")}</StateTitle>
          <StateMassage>{t("使い方.利用状況説明")}</StateMassage>
          <StateContent>
            <StateContentPanel>
              <Cross width="20px" height="20px" style={{minWidth: '20px'}} />
              <CloseLabel>
              {t("使い方.空きなし")}
              </CloseLabel>
              <StateContentInfo>{t("使い方.利用不可")}</StateContentInfo>
            </StateContentPanel>
            <StateContentPanel>
              <Circle width="20px" height="20px" style={{minWidth: '20px'}} />
              <OpenLabel>
              {t("使い方.空きあり")}
              </OpenLabel>
              <StateContentInfo>{t("使い方.利用可能")}</StateContentInfo>
            </StateContentPanel>
          </StateContent>
        </StatePanel>
        <DescriptionPanel>
          <DescriptionTitle>{t("使い方.設備説明")}</DescriptionTitle>
          <DescriptionContent>
            {adjustmentWcInfoList?.map((elm) => (
              <UsageLabel
                key={elm.type}
                type={elm.type}
                icon={elm.icon}
                description={elm.description}
              />
            ))}
          </DescriptionContent>
        </DescriptionPanel>
      </DialogContent>
    </Dialog>
  );
});
